import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, PlusCircle, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { trackOnboarding } from "@/lib/onboardingTrack";

const DISMISS_KEY = "pfo_empty_cta_dismissed_v1";

/**
 * EmptyDashboardCTA — one-time welcome card for brand-new users whose
 * dashboard has no financial data yet. Two clear paths:
 *   1. "Try with sample data" → POST /demo/seed?force=true and reload.
 *   2. "Add my first expense" → navigate to /expenses with autoOpenAdd.
 *
 * Also has a small "×" to dismiss (persisted in localStorage).
 *
 * Rendered by Dashboard.jsx above the OverviewGrid when
 * `summary.monthly_income === 0 && summary.monthly_expense === 0`.
 * Non-invasive: once the user has ANY financial data, this component
 * simply won't render again.
 */
export default function EmptyDashboardCTA({ userName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [seeding, setSeeding] = useState(false);

  const greeting = useMemo(() => {
    const name = userName?.split(" ")[0];
    return name
      ? t("empty_cta.welcome_named", { defaultValue: `Welcome, ${name}!`, name })
      : t("empty_cta.welcome", { defaultValue: "Welcome to Finora!" });
  }, [userName, t]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.post("/demo/seed?force=true");
      trackOnboarding("empty_cta_seeded");
      toast.success(t("empty_cta.seed_ok", { defaultValue: "Sample data loaded!" }));
      dismiss();
      // Hard-reload the dashboard so all data hooks re-fetch cleanly.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sample data");
      setSeeding(false);
    }
  };

  const addFirst = () => {
    trackOnboarding("empty_cta_add_expense");
    dismiss();
    navigate("/expenses", { state: { autoOpenAdd: Date.now() } });
  };

  if (dismissed) return null;

  return (
    <div
      className="relative card-flat p-6 lg:p-8 mb-6 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.08) 100%)",
        borderColor: "rgba(45,90,64,0.2)",
      }}
      data-testid="empty-dashboard-cta"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss welcome"
        data-testid="empty-cta-dismiss"
        className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-accent)] transition-colors"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-4 mb-5">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(135deg, var(--accent-primary), #2D5A40)" }}
        >
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl lg:text-2xl font-heading font-medium mb-1 text-[var(--text-primary)]">
            {greeting}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed max-w-xl">
            {t("empty_cta.body", {
              defaultValue: "Your dashboard is empty. Load a realistic sample profile in one tap to explore every feature, or start fresh with your own numbers.",
            })}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={seedDemo}
          disabled={seeding}
          data-testid="empty-cta-seed-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white shadow-sm active:scale-95 transition-transform disabled:opacity-70"
          style={{ background: "linear-gradient(135deg, var(--accent-primary), #2D5A40)" }}
        >
          {seeding ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {seeding
            ? t("empty_cta.seeding", { defaultValue: "Loading sample data…" })
            : t("empty_cta.seed_btn", { defaultValue: "Try with sample data" })}
        </button>
        <button
          onClick={addFirst}
          disabled={seeding}
          data-testid="empty-cta-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors border-[var(--border-default)] text-[var(--text-primary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]"
        >
          <PlusCircle size={16} />
          {t("empty_cta.add_btn", { defaultValue: "Add my first expense" })}
        </button>
      </div>

      <p className="text-[11px] text-[var(--text-muted)] mt-4">
        {t("empty_cta.privacy", {
          defaultValue: "Sample data is stored only in your account and can be cleared anytime from Settings → Reset my account.",
        })}
      </p>
    </div>
  );
}
